import * as React from "react";
import _ from "lodash";
import siteConfig from "site-config";
import { Intercom as IntercomSDK, shutdown, update } from "@intercom/messenger-js-sdk";
import type { UserArgs } from "@intercom/messenger-js-sdk/dist/types";
import { captureException } from "@sentry/browser";
import me from "models/me";
import type { IScope } from "reducers/scope";
import { AccountType } from "types/user";
import type { BaseMe } from "models/me";
import { isMobilePage, type IRouter } from "helpers/url/page";

const Intercom: React.FunctionComponent<IProps> = ({ hide, scope, router }) => {
    const [loaded, setLoaded] = React.useState(false);
    const updateIntercomPayload = React.useRef({});
    const isHidden = React.useRef(hide);
    const handleShutdown = () => {
        shutdown();
        setLoaded(false);
    };

    React.useEffect(
        () => {
            // If the user is impersonated, if it's a mobile view or if we miss critical information
            // we don't want to load intercom
            if (me?.actual_user || isMobilePage(router) || !me?.intercom_hash || !me?.email) {
                if (loaded) {
                    handleShutdown();
                }
                return;
            }

            const { intercom_hash, email, name, created_on, account_type, locale } = me;
            try {
                IntercomSDK({
                    app_id: siteConfig.INTERCOM,
                    email,
                    name,
                    user_hash: intercom_hash,
                    user_role: account_type,
                    created_at: new Date(created_on).getTime(),
                    language_override: locale,
                    hide_default_launcher: isHidden.current,
                });

                setLoaded(true);
            } catch (err) {
                captureException({
                    err,
                    message: "Intercom failed to load",
                    user_context: { id: me?._id },
                });
                return;
            }

            return () => {
                handleShutdown();
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    React.useEffect(() => {
        if (loaded && isHidden.current !== hide) {
            isHidden.current = hide;
            updateIntercom({
                hide_default_launcher: isHidden.current,
            });
        }
    }, [hide, loaded]);

    React.useEffect(() => {
        if (me.is_logging_out) {
            handleShutdown();
            return;
        }

        if (!loaded) {
            return;
        }

        const tempUpdateIntercomPayload = computeIntercomUpdateRequest({ me, scope });
        if (_.isEqual(tempUpdateIntercomPayload, updateIntercomPayload.current)) {
            return;
        }

        updateIntercomPayload.current = tempUpdateIntercomPayload;
        tempUpdateIntercomPayload && updateIntercom(tempUpdateIntercomPayload);
    }, [scope, loaded]);

    return null;
};

interface IProps {
    hide: boolean;
    scope: IScope;
    router: IRouter;
}

interface IIntercomCustomProps {
    brand_user_role: string;
    company: {
        company_id?: string;
        name?: string;
        type?: "retailer" | "company" | "brand";
        address_line_1?: string;
        address_line_2?: string;
        city?: string;
        state?: string;
        zip?: string;
        country?: string;
        license_status?: string;
    };
    active_company_id?: string;
}

// Proxy Method for type safety
const updateIntercom = (params: UserArgs & Partial<IIntercomCustomProps>) => update(params);

const accessAddressObject = (company: any) => {
    if (company?.contact_information?.address) {
        return company.contact_information.address;
    }

    if (company?.__addresses?.[0]) {
        return company.__addresses[0];
    }

    return company?.addresses?.[0];
};

const computeIntercomUpdateRequest = ({
    me,
    scope,
}: {
    me: BaseMe;
    scope: IScope;
}): UserArgs & Partial<IIntercomCustomProps> => {
    let company: IScope["brand"] | IScope["company"] | BaseMe["retailers"][0];
    const payload: UserArgs & Partial<IIntercomCustomProps> = {
        brand_user_role: null,
        company: null,
        active_company_id: null,
    };

    // For brand Users, we send state.scope.brand data to intercom
    // If a buyer has a company, we send the company to intercom
    // If a buyer has no a company, but retailer information, we send the retailer to intercom
    if (me?.account_type === AccountType.BRAND && scope?.brand?._id) {
        payload.brand_user_role = scope.brand?._users?.find((u) => u.ref === me._id)?.role;
        company = scope.brand;
    } else if (me?.account_type === AccountType.BUYER) {
        company = scope?.company || me?.retailers?.[0];
    }

    if (company?._id) {
        payload.active_company_id = company._id;
        const address = accessAddressObject(company);
        const tempCompany = {
            company_id: company._id,
            name: company?.name || company?.__name,
            address_line_1: address?.line1 || address?.line_1 || address?.__line_1,
            address_line_2: address?.line2 || address?.line_2 || address?.__line_2,
            city: address?.city || address?.__city,
            state: address?.state || address?.__state,
            zip: address?.zip || address?.__zip,
            country: address?.country || address?.__country,
            license_status: company?.settings?.subscription_tier,
            type: me?.account_type === AccountType.BRAND ? "brand" : scope?.company ? "company" : "retailer",
        };

        payload.company = _.pickBy(tempCompany, (key) => key !== undefined);
    }

    return payload;
};

export default Intercom;
